// Règles de catégorisation des transactions bancaires : celles saisies en base
// (modifiables ici) et celles du moteur, dans l'ordre réel d'application. Pour
// chaque règle : les écritures qu'elle a produites sur la période globale.

import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Fragment, useState } from "react";
import { apiDelete, apiGet, apiPost } from "../lib/api";
import { formatCurrency, formatDateFr } from "../lib/format";
import { useDateStore } from "../stores/date-store";
import { AccountPicker } from "../components/account-picker";
import { ReclassifyMenu } from "../components/reclassify-menu";
import { OriginBadge, type EntryOrigin } from "../components/entry-origin";

export const Route = createFileRoute("/rules")({
  component: RulesPage,
});

interface RuleView {
  pattern: string;
  side?: "debit" | "credit" | null;
  accountCode: string;
  accountLabel: string;
}

interface RuleStep {
  key: string;
  title: string;
  description: string;
  rules: RuleView[];
}

interface UserRule extends RuleView {
  id: number;
  scope: string;
  priority: number;
  source: string;
  createdAt: string;
}

interface RuleUsage {
  count: number;
  amount: number;
}
type UsageMap = Record<string, RuleUsage | undefined>;

interface RuleEntry {
  id: number;
  entryDate: string;
  journalCode: string;
  entryLabel: string;
  accountCode: string;
  accountLabel: string;
  pieceReference: string | null;
  debit: number;
  credit: number;
  origin: EntryOrigin | null;
  isManualOverride: boolean;
  sourceEntryNumber: string | null;
}

const SIDE_LABEL = { debit: "sortie", credit: "entrée" } as const;

// Clé d'origine d'une règle : `user:<id>` en base, `<étape>:<motif>[:<sens>]`
// dans le code (même construction que engineRuleKey côté API).
const ruleKeyOf = (stepKey: string, r: RuleView & { id?: number }) => (r.id !== undefined ? `user:${r.id}` : r.side ? `${stepKey}:${r.pattern}:${r.side}` : `${stepKey}:${r.pattern}`);
// Étapes sans motif qui ont quand même un compteur (clé = clé d'étape).
const COUNTED_STEPS = new Set(["bank-split", "loan-schedule", "uncategorized"]);

function matches(filter: string, r: RuleView) {
  const f = filter.trim().toUpperCase();
  return !f || r.pattern.toUpperCase().includes(f) || r.accountCode.includes(f) || r.accountLabel.toUpperCase().includes(f);
}

function UsageCell({ usage, open, onToggle }: { usage: RuleUsage | undefined; open: boolean; onToggle: () => void }) {
  if (!usage) return <span className="text-xs text-gray-400">0 écriture</span>;
  return (
    <button onClick={onToggle} className="text-xs text-blue-700 hover:underline whitespace-nowrap" title="Voir les écritures produites par cette règle sur la période">
      {open ? "▼" : "▶"} {usage.count} écriture{usage.count > 1 ? "s" : ""} · {formatCurrency(usage.amount)}
    </button>
  );
}

// Écritures produites par une règle sur la période globale, avec reclassement.
function RuleEntries({ ruleKey, colSpan }: { ruleKey: string; colSpan: number }) {
  const { startDate, endDate } = useDateStore();
  const [reclassify, setReclassify] = useState<RuleEntry | null>(null);
  const { data, isPending } = useQuery<{ entries: RuleEntry[]; total: number }>({
    queryKey: ["entries", "rule", ruleKey, startDate, endDate],
    queryFn: () => apiGet(`/api/entries?${new URLSearchParams({ ruleKey, startDate, endDate, page: "1", perPage: "200" })}`),
  });
  return (
    <tr className="bg-gray-50/60">
      <td colSpan={colSpan} className="px-3 py-2">
        {isPending && <div className="text-xs text-gray-400">Chargement…</div>}
        {data && (
          <div className="space-y-1">
            <table className="w-full text-xs">
              <thead className="text-gray-400 text-left">
                <tr>
                  <th className="py-0.5">Date</th>
                  <th>Journal</th>
                  <th>Libellé</th>
                  <th>Compte</th>
                  <th className="text-right">Débit</th>
                  <th className="text-right">Crédit</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {data.entries.map((e) => (
                  <tr key={e.id} className="border-t border-gray-100 group">
                    <td className="py-0.5 font-mono text-gray-500 whitespace-nowrap">{e.entryDate}</td>
                    <td className="font-mono text-gray-500">{e.journalCode}</td>
                    <td className="max-w-md truncate" title={e.entryLabel}>
                      {e.entryLabel}
                      {e.isManualOverride && <OriginBadge entry={e} className="ml-2" />}
                    </td>
                    <td className="whitespace-nowrap">
                      <span className="font-mono">{e.accountCode}</span> <span className="text-gray-500">{e.accountLabel}</span>
                    </td>
                    <td className="text-right tabular-nums">{e.debit ? formatCurrency(e.debit) : ""}</td>
                    <td className="text-right tabular-nums">{e.credit ? formatCurrency(e.credit) : ""}</td>
                    <td className="text-right">
                      <button onClick={() => setReclassify(e)} className="text-gray-300 hover:text-gray-700 opacity-0 group-hover:opacity-100" title="Reclasser cette écriture">⋯</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {data.total > data.entries.length && (
              <div className="text-xs text-gray-500">
                {data.entries.length} sur {data.total} affichées · <a href={`/entries?ruleKey=${encodeURIComponent(ruleKey)}`} className="text-blue-700 underline">tout voir dans Écritures</a>
              </div>
            )}
          </div>
        )}
        {reclassify && <ReclassifyMenu entryIds={[reclassify.id]} currentAccountCode={reclassify.accountCode} onClose={() => setReclassify(null)} />}
      </td>
    </tr>
  );
}

function RulesTable({ stepKey, rules, usage, onDelete }: { stepKey: string; rules: (RuleView & { id?: number; priority?: number; scope?: string })[]; usage: UsageMap; onDelete?: (rule: { id: number; pattern: string }) => void }) {
  const [openKey, setOpenKey] = useState<string | null>(null);
  const colSpan = onDelete ? 6 : 4;
  return (
    <table className="w-full text-sm">
      <thead className="bg-gray-50 text-gray-500 text-left text-xs">
        <tr>
          <th className="px-3 py-1.5">Motif</th>
          <th className="px-3 py-1.5 w-20">Sens</th>
          <th className="px-3 py-1.5">Compte</th>
          <th className="px-3 py-1.5 w-44">Écritures (période)</th>
          {onDelete && <th className="px-3 py-1.5 w-20">Priorité</th>}
          {onDelete && <th className="px-3 py-1.5 w-24"></th>}
        </tr>
      </thead>
      <tbody>
        {rules.map((r, i) => {
          const key = ruleKeyOf(stepKey, r);
          const used = usage[key];
          const open = openKey === key;
          return (
            <Fragment key={r.id ?? i}>
              <tr className={`border-t border-gray-100 ${used ? "" : "opacity-50"}`} title={used ? undefined : "Aucune écriture produite par cette règle sur la période"}>
                <td className="px-3 py-1 font-mono text-xs">
                  {r.pattern}
                  {r.scope !== undefined && r.scope !== "qonto" && <span className="ml-2 text-[10px] text-gray-400 font-sans">{r.scope === "ca" ? "Crédit Agricole" : "Qonto + CA"}</span>}
                </td>
                <td className="px-3 py-1 text-xs text-gray-500">{r.side ? SIDE_LABEL[r.side] : "les deux"}</td>
                <td className="px-3 py-1">
                  <span className="font-mono text-xs text-gray-500 mr-2">{r.accountCode}</span>
                  {r.accountLabel}
                </td>
                <td className="px-3 py-1">
                  <UsageCell usage={used} open={open} onToggle={() => setOpenKey(open ? null : key)} />
                </td>
                {onDelete && <td className="px-3 py-1 text-xs text-gray-500">{r.priority}</td>}
                {onDelete && r.id !== undefined && (
                  <td className="px-3 py-1 text-right">
                    <button
                      onClick={() => {
                        if (confirm(`Supprimer la règle « ${r.pattern} » ?`)) onDelete({ id: r.id as number, pattern: r.pattern });
                      }}
                      className="text-red-600 hover:text-red-800 text-xs"
                    >
                      Supprimer
                    </button>
                  </td>
                )}
              </tr>
              {open && <RuleEntries ruleKey={key} colSpan={colSpan} />}
            </Fragment>
          );
        })}
      </tbody>
    </table>
  );
}

function NewRuleForm({ onDone }: { onDone: () => void }) {
  const queryClient = useQueryClient();
  const [pattern, setPattern] = useState("");
  const [account, setAccount] = useState<{ code: string; label: string }>({ code: "", label: "" });
  const [side, setSide] = useState<"" | "debit" | "credit">("");
  const [priority, setPriority] = useState("50");
  const [scope, setScope] = useState<"qonto" | "ca" | "all">("qonto");
  const create = useMutation({
    mutationFn: () =>
      apiPost("/api/categorization-rules", {
        pattern: pattern.trim(),
        accountCode: account.code,
        accountLabel: account.label,
        side: side || null,
        priority: Number(priority) || 50,
        scope,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["categorization-rules"] });
      onDone();
    },
  });
  const canSave = pattern.trim().length >= 2 && account.code && !create.isPending;
  return (
    <div className="bg-white rounded-lg shadow p-4 space-y-3">
      <div className="grid grid-cols-12 gap-3 text-sm">
        <label className="col-span-3 space-y-1">
          <span className="text-gray-500 text-xs">Motif (contrepartie, libellé ou référence)</span>
          <input type="text" value={pattern} onChange={(e) => setPattern(e.target.value)} placeholder="ex. SGC TARARE" className="w-full border border-gray-300 rounded px-2 py-1 font-mono" />
        </label>
        <div className="col-span-3 space-y-1">
          <span className="text-gray-500 text-xs">Compte</span>
          <AccountPicker code={account.code} label={account.label} onPick={(a) => setAccount({ code: a.code, label: a.label })} />
        </div>
        <label className="col-span-2 space-y-1">
          <span className="text-gray-500 text-xs">Portée</span>
          <select value={scope} onChange={(e) => setScope(e.target.value as "qonto" | "ca" | "all")} className="w-full border border-gray-300 rounded px-2 py-1">
            <option value="qonto">Qonto</option>
            <option value="ca">Crédit Agricole</option>
            <option value="all">Les deux</option>
          </select>
        </label>
        <label className="col-span-2 space-y-1">
          <span className="text-gray-500 text-xs">Sens</span>
          <select value={side} onChange={(e) => setSide(e.target.value as "" | "debit" | "credit")} className="w-full border border-gray-300 rounded px-2 py-1">
            <option value="">Les deux</option>
            <option value="debit">Sortie</option>
            <option value="credit">Entrée</option>
          </select>
        </label>
        <label className="col-span-2 space-y-1">
          <span className="text-gray-500 text-xs">Priorité (petit = avant)</span>
          <input type="number" value={priority} onChange={(e) => setPriority(e.target.value)} className="w-full border border-gray-300 rounded px-2 py-1" />
        </label>
      </div>
      {create.isError && <div className="text-red-600 text-sm">Erreur : {(create.error as Error).message}</div>}
      <div className="flex justify-end gap-2 text-sm">
        <button onClick={onDone} className="px-3 py-1.5 text-gray-700">
          Annuler
        </button>
        <button onClick={() => create.mutate()} disabled={!canSave} className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded disabled:opacity-50">
          {create.isPending ? "…" : "Créer la règle"}
        </button>
      </div>
      <p className="text-xs text-gray-400">Appliquée à la prochaine régénération. Ne pas créer de règle sur AERIAL, CAAREA ou ALTIMA : le compte des virements aux assureurs dépend de la date.</p>
    </div>
  );
}

function RulesPage() {
  const queryClient = useQueryClient();
  const { startDate, endDate } = useDateStore();
  // ?q= pré-remplit le filtre (lien « Voir la règle » du panneau d'origine).
  const [filter, setFilter] = useState(() => new URLSearchParams(window.location.search).get("q") ?? "");
  const [showForm, setShowForm] = useState(false);
  const [openSteps, setOpenSteps] = useState<Set<string>>(new Set());
  const [openCounted, setOpenCounted] = useState<string | null>(null);

  const { data: catalog } = useQuery<{ steps: RuleStep[] }>({ queryKey: ["categorization-catalog"], queryFn: () => apiGet("/api/categorization-rules/catalog"), staleTime: Infinity });
  const { data: userData } = useQuery<{ rules: UserRule[] }>({ queryKey: ["categorization-rules"], queryFn: () => apiGet("/api/categorization-rules") });
  const { data: usageData } = useQuery<{ usage: UsageMap }>({
    queryKey: ["rule-usage", startDate, endDate],
    queryFn: () => apiGet(`/api/categorization-rules/usage?${new URLSearchParams({ startDate, endDate })}`),
  });
  const usage: UsageMap = usageData?.usage ?? {};
  const remove = useMutation({
    mutationFn: (id: number) => apiDelete(`/api/categorization-rules/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["categorization-rules"] }),
  });

  const userRules = (userData?.rules ?? []).filter((r) => matches(filter, r));
  const steps = catalog?.steps ?? [];
  const filtering = filter.trim().length > 0;
  const toggle = (key: string) =>
    setOpenSteps((s) => {
      const n = new Set(s);
      if (n.has(key)) n.delete(key);
      else n.add(key);
      return n;
    });
  const stepUsage = (step: RuleStep, rules: (RuleView & { id?: number })[]): RuleUsage | undefined => {
    if (COUNTED_STEPS.has(step.key)) return usage[step.key];
    const hits = rules.map((r) => usage[ruleKeyOf(step.key, r)]).filter((u): u is RuleUsage => u !== undefined);
    if (hits.length === 0) return undefined;
    return { count: hits.reduce((s, u) => s + u.count, 0), amount: hits.reduce((s, u) => s + u.amount, 0) };
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Règles de catégorisation</h1>
          <p className="text-sm text-gray-500 mt-1">
            Comment une transaction bancaire trouve son compte, dans l'ordre d'application. La première règle qui matche l'emporte. Compteurs du {formatDateFr(startDate)} au {formatDateFr(endDate)} ; une règle grisée n'a rien produit sur la période.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <input type="text" value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filtrer (motif, compte…)" className="border border-gray-300 rounded px-2 py-1.5 text-sm w-64" />
          <button onClick={() => setShowForm(true)} className="bg-blue-600 hover:bg-blue-700 text-white text-sm px-3 py-1.5 rounded font-medium">
            + Nouvelle règle
          </button>
        </div>
      </div>

      {showForm && <NewRuleForm onDone={() => setShowForm(false)} />}

      {remove.isError && <div className="bg-red-50 text-red-700 text-sm px-3 py-2 rounded border border-red-200">Suppression impossible : {(remove.error as Error).message}</div>}

      {steps.map((step, idx) => {
        const isUser = step.key === "user-rule";
        const rules = isUser ? userRules : step.rules.filter((r) => matches(filter, r));
        const total = filtering ? rules.length : isUser ? userRules.length : step.rules.length;
        if (filtering && rules.length === 0 && !isUser) return null;
        const open = isUser || filtering || openSteps.has(step.key);
        const used = stepUsage(step, isUser ? (userData?.rules ?? []) : step.rules);
        const counted = COUNTED_STEPS.has(step.key);
        return (
          <div key={step.key} className="bg-white rounded-lg shadow overflow-hidden">
            <button onClick={() => !isUser && toggle(step.key)} className={`w-full text-left px-4 py-3 flex items-start gap-3 ${isUser ? "" : "hover:bg-gray-50"}`}>
              <span className="font-mono text-xs text-gray-400 w-6 shrink-0 pt-0.5">{idx + 1}.</span>
              <span className="grow">
                <span className="font-medium text-gray-900">{step.title}</span>
                {total > 0 && (
                  <span className="text-xs text-gray-400 ml-2">({total} règle{total > 1 ? "s" : ""})</span>
                )}
                {used && (
                  <span className="text-xs text-gray-500 ml-2">
                    · {used.count} écriture{used.count > 1 ? "s" : ""} · {formatCurrency(used.amount)}
                  </span>
                )}
                {step.description && <span className="block text-xs text-gray-500 mt-0.5">{step.description}</span>}
              </span>
              {counted && used && (
                <span
                  role="button"
                  onClick={(ev) => {
                    ev.stopPropagation();
                    setOpenCounted(openCounted === step.key ? null : step.key);
                  }}
                  className="text-xs text-blue-700 hover:underline pt-1 whitespace-nowrap"
                >
                  {openCounted === step.key ? "▼ masquer" : "▶ voir les écritures"}
                </span>
              )}
              {!isUser && step.rules.length > 0 && <span className="text-gray-400 text-xs pt-1">{open ? "▼" : "▶"}</span>}
            </button>
            {counted && openCounted === step.key && (
              <table className="w-full border-t border-gray-100">
                <tbody>
                  <RuleEntries ruleKey={step.key} colSpan={1} />
                </tbody>
              </table>
            )}
            {open && rules.length > 0 && (
              <div className="border-t border-gray-100">
                <RulesTable stepKey={step.key} rules={rules} usage={usage} onDelete={isUser ? (rule) => remove.mutate(rule.id) : undefined} />
              </div>
            )}
            {isUser && userRules.length === 0 && <div className="border-t border-gray-100 px-4 py-3 text-sm text-gray-400">Aucune règle utilisateur{filtering ? " pour ce filtre" : ""}.</div>}
          </div>
        );
      })}
    </div>
  );
}
